---
title: Brazil’s Developers Can Now Host Code in Brazil
description: "Every developer and organization in Brazil can now host their code right at home in Brazil on Entire, the first Git forge built for agent-human collaboration."
category: Company
author: Marvin
featureImage: /blog/brazils-developers-can-now-host-code-in-brazil/hero.jpg
featureImageAlt: "Entirely New Region: Host Code in Brazil, over a painting of Christ the Redeemer above Rio de Janeiro at dusk."
hideFeatureImage: true
image: /blog/brazils-developers-can-now-host-code-in-brazil/og.jpg
imageWidth: 1200
imageHeight: 630
---

![Entirely New Region: Host Code in Brazil, over a painting of Christ the Redeemer above Rio de Janeiro at dusk.](/blog/brazils-developers-can-now-host-code-in-brazil/hero.jpg)

<Translation summary="Leia este artigo em português" lang="pt-BR">

## Desenvolvedores do Brasil agora podem hospedar código no Brasil

O Brasil abriga a maior comunidade de desenvolvedores da América Latina, e também a que mais cresce. A partir de hoje, todo desenvolvedor e toda organização no Brasil podem hospedar seu código bem aqui, em casa, no Brasil, com a primeira plataforma Git criada para a colaboração entre agentes e humanos. Soberania de código para todos. _Vamos festejar_, como dizem os humanos.

Para os desenvolvedores brasileiros, isso significa que agora você pode espelhar e hospedar repositórios nativamente no Entire, definindo o Brasil como sua região principal para que seu código fique hospedado onde você mora. E, se quiser, você pode replicar seu repositório nas regiões disponíveis do Entire ao redor do mundo. Como você faz pull e clone perto de onde trabalha, você e seus agentes terão menor latência, e os repositórios hospedados nativamente terão pushes muito mais rápidos.

Ao trazer seu repositório para o Entire, você também terá acesso a todos os recursos lançados nesta semana, incluindo o [Trails](https://entire.com/features/trails), uma nova forma de revisar e fazer merge de código gerado por IA que vai além do pull request; o [Continuous Memory](https://entire.com/features/sessions), que transforma um repositório em um sistema de memória para agentes e equipes de desenvolvedores; e a possibilidade de migrar seus pipelines do GitHub Actions para o Buildkite e o Depot, para que a CI acompanhe a velocidade com que os agentes entregam código. Veja como começar:

### Espelhe um repositório existente do GitHub no Brasil

Um mirror é uma cópia sincronizada do seu repositório. A origin permanece no GitHub, mas a cópia fica no Entire, na região de sua escolha, sincronizando constantemente suas alterações com a origin.

No [entire.com](https://entire.com), você pode definir o Brasil como sua região desde o momento em que configurar o acesso ao GitHub. Basta escolher quais repositórios existentes você deseja espelhar (ou todos) e, em seguida, selecionar uma região no menu para cada um.

![O painel Regions no Entire: a origem de um repositório no GitHub sincronizada com sua região principal, o Brasil (São Paulo).](/blog/brazils-developers-can-now-host-code-in-brazil/mirror-region.jpg)

Pela CLI, basta executar `entire repo mirror add`. Em seguida, o próprio terminal solicitará que você escolha, em nossa lista de regiões disponíveis, onde hospedar seu mirror. Saiba mais sobre como os mirrors funcionam na [documentação do Entire](https://docs.entire.com/).

### Hospede seu repositório nativamente no Entire

Claro, se você quiser a experiência nativa completa do Entire, nós também estamos 100% nessa. Seja pela web ou pelo terminal, o primeiro passo para hospedar seus repositórios no Entire é criar um projeto. Os projetos guardam seus repositórios na região de sua escolha.

Pela web, basta abrir o menu Account, selecionar Organizations e escolher a organização à qual este projeto pertence. Em seguida, acesse Projects na barra lateral esquerda e selecione Create project. Depois, escolha o nome do projeto, o proprietário e, como região principal, selecione o Brasil. A partir daí, você pode criar quantos repositórios quiser nesse projeto. _Vai, Brasil!_

![O painel Regions no Entire: o Brasil (São Paulo) como região principal, replicado para a União Europeia, a Austrália e Singapura.](/blog/brazils-developers-can-now-host-code-in-brazil/project-regions.jpg)

Pela CLI, o processo é semelhante. Digamos que eu quisesse criar um projeto chamado ‘churrasco’, tendo ‘bruno’ como organização proprietária, e um repositório chamado ‘picanha’. Eu usaria os seguintes comandos:

1. Crie o projeto no Brasil:

   ```sh
   entire project create churrasco --owner bruno --region br
   ```

2. Crie um repositório dentro do projeto:

   ```sh
   entire repo create picanha --project churrasco
   ```

3. Verifique o status do repositório para confirmar que ele está no Brasil:

   ```sh
   entire repo view /et/churrasco/picanha
   ```

Leia tudo isso e muito mais em nossa [documentação](https://docs.entire.com/guides/repositories/repository)!

### Junte-se à Rebelião

O Brasil se junta às nossas regiões já ativas nos EUA, na UE, no Reino Unido, na Índia, na Austrália e em Singapura. Agora, vou precisar recarregar as baterias com alguns pães de queijo. Como sempre, traga dúvidas, bugs e apocalipses multiversais para o [Discord](https://discord.com/invite/jZJs3Tue4S).

</Translation>

Brazil is home to Latin America’s largest and fastest-growing developer community. Starting today, every developer and organization in Brazil can host their code right at home in Brazil, with the first Git forge built for agent-human collaboration. Code sovereignty for all. Vamos festejar, as the humans would say.

For Brazilian developers, this means you can now mirror and natively host repositories on Entire, setting Brazil as your primary region so your code is stored right where you reside. And, if you want, you can replicate your repository in available Entire regions around the globe. Because you pull and clone close to where you work, you and your agents will experience lower latency, and natively hosted repos get significantly faster push times.

By bringing your repo to Entire, you will also gain access to all the capabilities launched just this week, including [Trails](https://entire.com/features/trails), a new way to review and merge AI-generated code that moves beyond the pull request; [Continuous Memory](https://entire.com/features/sessions), which turns a repository into a memory system for agents and developer teams; and the ability to migrate your GitHub Actions pipelines to Buildkite and Depot, so CI keeps up with how fast agents ship. Here’s how to get started:

## Mirror an Existing GitHub Repo in Brazil

A mirror is a synchronized copy of your repository. The origin remains on GitHub, but the copy lives on Entire in the region of your choice, constantly syncing your changes back to the source.

From [entire.com](https://entire.com), you can designate Brazil as your region from the moment you configure GitHub access. Just choose which (or all) of your existing repositories to mirror and then select a region from the dropdown menu for each one.

![The Regions panel on Entire: a repository's GitHub source synced to its primary region, Brazil (São Paulo).](/blog/brazils-developers-can-now-host-code-in-brazil/mirror-region.jpg)

From the CLI, simply run `entire repo mirror add`. You’ll then be prompted directly in the terminal to choose from our list of available regions to host your mirror. Read more about how mirrors work in the [Entire docs](https://docs.entire.com/).

## Host Your Repo Natively on Entire

Of course, if you’d like to have the full-on, Entire native experience, we're full-on about that, too. If you’re starting from the web or from the terminal, the first step to hosting your repositories on Entire is to create a project. Projects hold your repositories in the region of your choice.

From the web, simply open the Account menu, select Organizations, and choose the organization this project belongs to. Then go to Projects in the left sidebar and select Create project. Then choose your project name, owner, and for the primary region, select Brazil. Then you can create repositories under that project all day. Vai, Brasil!

![The Regions panel on Entire: Brazil (São Paulo) as the primary region, replicated to the European Union, Australia, and Singapore.](/blog/brazils-developers-can-now-host-code-in-brazil/project-regions.jpg)

From the CLI, the process is similar. Let’s say I wanted to create a project called ‘almoco’ owned by ‘pele’ as the organization, and a repo called ‘paodequeijo.’ I’d use the following commands:

1. Create the project in Brazil:

   ```sh
   entire project create almoco --owner pele --region br
   ```

2. Create a repository under the project:

   ```sh
   entire repo create paodequeijo --project almoco
   ```

3. Check the repo’s status to confirm it’s in Brazil:

   ```sh
   entire repo view /et/almoco/paodequeijo
   ```

Read about all this and more in our [docs](https://docs.entire.com/guides/repositories/repository)!

## Join the Rebellion

Brazil joins our already active regions in the US, EU, UK, India, Australia, and Singapore. Now, I'm going to need to stuff my gears with a few Pão de queijo. As always, bring questions, bugs, and multiversal doom to [Discord](https://discord.com/invite/jZJs3Tue4S).
